import { DashboardV1State } from "pages/api/deep-links/types";
import { Dispatch, MutableRefObject, SetStateAction } from "react";
import { useDashboards } from "src/hooks/useDashboards";
import { SearchState } from "src/search/types";

export type DashboardsPageContext = {
  dashboardId?: string | typeof START_STATE_ID | typeof UNSAVED_ID;
  openDashboard(id: string): void;
  isLocked: boolean;
  unlock(): void;
  lock(): void;
  collection: ReturnType<typeof useDashboards>;
  runtime: MutableRefObject<DashboardRuntime | undefined>;
};

export type DashboardRuntime = {
  source: "dashboard" | "embedded";
  extra: Partial<Omit<DashboardV1State, "layout" | "data">>;
  layout: DashboardV1State["layout"];
  data: DashboardV1State["data"];
  name: string;
  description: string;
  thumbnail?: string;
  scope?: "private" | "workspace" | "global";
  setScope: Dispatch<
    SetStateAction<undefined | "private" | "workspace" | "global">
  >;
  setName: Dispatch<SetStateAction<string>>;
  setData: Dispatch<SetStateAction<DashboardV1State["data"]>>;
  setDescription: Dispatch<SetStateAction<string>>;
  setLayout: Dispatch<SetStateAction<DashboardV1State["layout"]>>;
  setExtra: Dispatch<
    SetStateAction<Partial<Omit<DashboardV1State, "layout" | "data">>>
  >;
  mutationInProcess: boolean;
  searchState: Partial<SearchState>;
  setSearchState: Dispatch<SetStateAction<Partial<SearchState>>>;
  onSave(
    values?: Partial<
      {
        name: string;
        description: string;
        thumbnail?: string;
        scope: "private" | "workspace" | "global";
      } & DashboardV1State
    >
  ): Promise<void>;
  onCancel(): void;
  onDelete(): void;
  onRemoveCard(id: string): void;
  setEditor: Dispatch<SetStateAction<JSX.Element | null>>;
  userCanEdit: boolean;
  userCanCreate: boolean;
  readyToEdit: boolean;
};

export const staticDashboardsPageContext: DashboardsPageContext = {
  dashboardId: undefined,
  openDashboard() {},
  isLocked: true,
  unlock() {},
  lock() {},
  collection: {} as ReturnType<typeof useDashboards>,
  runtime: { current: undefined } as DashboardsPageContext["runtime"],
};

export const START_STATE_ID = "start";

export const UNSAVED_ID = "new";

export const UNSAVED_TITLE = "New Board";

export const isStartScreenId = (id?: string) =>
  id === START_STATE_ID || !Boolean(id);

export const isNewDashboardId = (id?: string) => id === UNSAVED_ID;

export const isDashboardId = (id?: string): id is string =>
  !isNewDashboardId(id) && !isStartScreenId(id);
